"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  useAuth,
  useGlobalStoreHydrated,
  useSelectionState,
  useUserChoices,
} from "@/state/globalStore";
import { showWarningToast } from "@/shared/feedback";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import type { GenericIconType } from "@/shared/ui/actions/GenericIcon";
import type { GenericOptionItem } from "@/shared/ui/controls/option.types";
import { MindstringPrototype, type ThoughtGrouping } from "@/generated/fastapi";
import appSettings from "@/shared/config/appSettings";
import { normalizeVisibleMindstringPrototype } from "@/shared/config/backend";
import useStudioProfileExport from "../hooks/useStudioProfileExport";
import {
  countSituationsByPrototype,
  EMPTY_PROTOTYPE_COUNTS,
  sortSituations,
  useSituationsQuery,
} from "@/features/situations";
import { PROFILE_SHAPING_MESSAGES } from "@/features/profile-shaping/config/actions";
import {
  useStudioIndexHits,
  type StudioIndexHitEntry,
} from "../contexts/StudioIndexHitsContext";
import useStudioClientReady from "../hooks/useStudioClientReady";
import { isStudioSituationLoadedFromState } from "../utils";

type StudioPrototypeOption = GenericOptionItem<MindstringPrototype> & {
  description: string;
  icon: GenericIconType;
  count: number;
};

type StudioQuickActionKey =
  | "select_visible"
  | "load_profile_version";

export type StudioQuickAction = {
  key: StudioQuickActionKey;
  label: string;
  icon: GenericIconType;
  humanReadable: string;
  disabled?: boolean;
};

export type StudioSituationLoadManagerItem = {
  situationId: string;
  title: string;
  description: string;
  isLoaded: boolean;
};

type VisibleSelectionScope = {
  situationEntries: {
    situationId: string;
    thoughtIds: string[];
  }[];
  freeThoughtIds: string[];
};

function getUniqueIds(ids: string[]) {
  return Array.from(new Set(ids));
}

function getVisibleSelectionScope({
  entries,
  activePrototypeCategory,
}: {
  entries: StudioIndexHitEntry[];
  activePrototypeCategory: MindstringPrototype;
}): VisibleSelectionScope {
  const activeStudioContent = "confirmed";

  return entries.reduce<VisibleSelectionScope>(
    (scope, entry) => {
      const parts = entry.indexId.split("-");

      if (parts[0] === "freelist") {
        const [, content, prototype] = parts;
        if (
          content === activeStudioContent &&
          prototype === activePrototypeCategory
        ) {
          scope.freeThoughtIds.push(...entry.thoughtIds);
        }
        return scope;
      }

      const [content, prototype, ...situationIdParts] = parts;
      if (
        content !== activeStudioContent ||
        prototype !== activePrototypeCategory
      ) {
        return scope;
      }

      const situationId = situationIdParts.join("-");
      if (!situationId) {
        return scope;
      }

      scope.situationEntries.push({
        situationId,
        thoughtIds: entry.thoughtIds,
      });
      return scope;
    },
    {
      situationEntries: [],
      freeThoughtIds: [],
    },
  );
}

export default function useStudioScreen() {
  const loadedSelectionSearchGuidanceSeenRef = useRef(false);
  const wasLoadedSelectionSearchGuidanceActiveRef = useRef(false);
  const { isUserAdmin } = useAuth();
  const { countSelectedSituations, countSelectedThoughts } =
    useSelectionState();
  const {
    activeStudioExportLock,
    toggleActiveStudioExportLock,
    canLockExport,
    setActivePrototypeCategory,
    activePrototypeCategory,
    activeStudioGrouping,
    setActiveStudioGrouping,
    setStudioSituationLoaded,
    setStudioSituationsLoaded,
    studioSituationLoadState,
    addSelectedSituation,
    selectThought,
    setInterimState,
    resetInterimState,
    interimState,
  } = useUserChoices();
  const {
    mindstringPrototypesSlangItems,
    studioGroupingItems,
  } = useOptionItems();
  const situationsQuery = useSituationsQuery();
  const { getIndexHitEntries } = useStudioIndexHits();
  const {
    isCheckingMoveOnReflections,
    isDownloadBlockedByMoveOn,
    moveOnReflectionBlockMessage,
  } = useStudioProfileExport();
  const hasStableClientState = useGlobalStoreHydrated();
  const hasClientReady = useStudioClientReady();
  const canUsePersistedStudioLoadState =
    hasStableClientState && hasClientReady;

  const activePrototypeCategoryForUi = normalizeVisibleMindstringPrototype(
    hasStableClientState
      ? activePrototypeCategory
      : appSettings.default.activePrototypeCategory,
    isUserAdmin,
  );
  const activeStudioGroupingForUi = hasStableClientState
    ? activeStudioGrouping
    : appSettings.default.activeStudioGrouping;

  const situationCountsByPrototype = useMemo(() => {
    if (!canUsePersistedStudioLoadState) {
      return countSituationsByPrototype(situationsQuery.data);
    }

    const counts = { ...EMPTY_PROTOTYPE_COUNTS };

    for (const situation of situationsQuery.data ?? []) {
      for (const prototype of situation.prototypes) {
        if (
          isStudioSituationLoadedFromState({
            loadState: studioSituationLoadState,
            prototype,
            situationId: situation.id,
          })
        ) {
          counts[prototype] += 1;
        }
      }
    }

    return counts;
  }, [
    canUsePersistedStudioLoadState,
    studioSituationLoadState,
    situationsQuery.data,
  ]);

  const studioSituationLoadManagerItems = useMemo(
    () =>
      sortSituations(situationsQuery.data ?? [])
        .filter((situation) =>
          situation.prototypes.includes(activePrototypeCategoryForUi),
        )
        .map((situation) => ({
          situationId: situation.id,
          title: situation.position || situation.abbr || "Untitled situation",
          description: situation.context,
          isLoaded:
            !canUsePersistedStudioLoadState ||
            isStudioSituationLoadedFromState({
              loadState: studioSituationLoadState,
              prototype: activePrototypeCategoryForUi,
              situationId: situation.id,
            }),
        })) satisfies StudioSituationLoadManagerItem[],
    [
      activePrototypeCategoryForUi,
      canUsePersistedStudioLoadState,
      studioSituationLoadState,
      situationsQuery.data,
    ],
  );
  const loadedSituationManagerItemCount = studioSituationLoadManagerItems.filter(
    (item) => item.isLoaded,
  ).length;
  const availableSituationManagerItemCount =
    studioSituationLoadManagerItems.length;
  const hiddenSituationManagerItemCount =
    availableSituationManagerItemCount - loadedSituationManagerItemCount;

  const studioPrototypeItemsWithCounts = useMemo(
    () =>
      mindstringPrototypesSlangItems.map((item) => ({
        ...item,
        count: situationCountsByPrototype[item.value],
      })) as StudioPrototypeOption[],
    [mindstringPrototypesSlangItems, situationCountsByPrototype],
  );

  const selectedSituationsCountForUi = hasStableClientState
    ? countSelectedSituations()
    : 0;
  const selectedThoughtsCountForUi = hasStableClientState
    ? countSelectedThoughts()
    : 0;
  const isLocked = hasStableClientState
    ? activeStudioExportLock
    : appSettings.default.activeStudioExportLock;
  const shouldShowManualAddGuidance =
    hasStableClientState &&
    interimState.profile_shaping_guidance?.isOpen === true &&
    interimState.profile_shaping_guidance?.value === "manual_add";
  const isLoadedSelectionSearchGuidanceActive =
    hasStableClientState &&
    interimState.profile_shaping_guidance?.isOpen === true &&
    interimState.profile_shaping_guidance?.value === "loaded_selection_search";
  const shouldShowLoadedSelectionSearchGuidance =
    isLoadedSelectionSearchGuidanceActive && !isLocked;
  const hasSelectionActionBar =
    selectedThoughtsCountForUi > 0 || selectedSituationsCountForUi > 0;
  const isPreviewUnavailable = false;
  const visibleSelectionScopeForUi = useMemo(
    () =>
      getVisibleSelectionScope({
        entries: getIndexHitEntries(),
        activePrototypeCategory: activePrototypeCategoryForUi,
      }),
    [activePrototypeCategoryForUi, getIndexHitEntries],
  );
  const visibleSituationsCountForUi = loadedSituationManagerItemCount;

  useEffect(() => {
    if (!hasStableClientState) {
      return;
    }

    if (activePrototypeCategoryForUi !== activePrototypeCategory) {
      setActivePrototypeCategory(activePrototypeCategoryForUi);
    }
  }, [
    activePrototypeCategory,
    activePrototypeCategoryForUi,
    hasStableClientState,
    setActivePrototypeCategory,
  ]);
  const visibleThoughtsCountForUi = getUniqueIds([
    ...visibleSelectionScopeForUi.freeThoughtIds,
    ...visibleSelectionScopeForUi.situationEntries.flatMap(
      (entry) => entry.thoughtIds,
    ),
  ]).length;

  const reviewDescription = (() => {
    if (!isLocked) {
      return PROFILE_SHAPING_MESSAGES.searchMode;
    }

    if (isCheckingMoveOnReflections) {
      return "Checking reflections for MoveOn-linked export.";
    }

    if (isDownloadBlockedByMoveOn) {
      return moveOnReflectionBlockMessage;
    }

    return PROFILE_SHAPING_MESSAGES.orderMode;
  })();

  const previewButtonColorPalette = isLocked ? "green" : "gray";
  const previewButtonIcon: GenericIconType = isLocked
    ? "search"
    : "rerank";
  const previewButtonHumanReadable = isLocked
    ? "Return to search mode"
    : "Enter order mode";
  const shouldShowReviewDescription = Boolean(reviewDescription);

  const isNeedsAnchorMode = false;
  const maintenanceModeHumanReadable = null;

  const handlePrototypeCategoryChange = useCallback(
    (value: MindstringPrototype) => {
      setActivePrototypeCategory(value);
    },
    [setActivePrototypeCategory],
  );
  const handleStudioGroupingChange = useCallback(
    (value: ThoughtGrouping) => {
      setActiveStudioGrouping(value);
    },
    [setActiveStudioGrouping],
  );
  const handleStudioQuickActionClick = useCallback(
    async (actionKey: StudioQuickActionKey) => {
      switch (actionKey) {
        case "select_visible": {
          const scope = getVisibleSelectionScope({
            entries: getIndexHitEntries(),
            activePrototypeCategory: activePrototypeCategoryForUi,
          });
          const visibleThoughtIds = getUniqueIds([
            ...scope.freeThoughtIds,
            ...scope.situationEntries.flatMap((entry) => entry.thoughtIds),
          ]);
          const hasVisibleSituations = scope.situationEntries.length > 0;
          const hasVisibleThoughts = visibleThoughtIds.length > 0;

          if (!hasVisibleSituations && !hasVisibleThoughts) {
            showWarningToast("No visible situations or thoughts to select.");
            return;
          }

          for (const entry of scope.situationEntries) {
            addSelectedSituation(
              activePrototypeCategoryForUi,
              entry.situationId,
            );
          }

          for (const entry of scope.situationEntries) {
            for (const thoughtId of entry.thoughtIds) {
              selectThought({
                prototypeCategory: activePrototypeCategoryForUi,
                situationId: entry.situationId,
                thoughtId,
              });
            }
          }

          for (const thoughtId of scope.freeThoughtIds) {
            selectThought({
              prototypeCategory: activePrototypeCategoryForUi,
              situationId: null,
              thoughtId,
            });
          }
          return;
        }
        case "load_profile_version":
          setInterimState("manage_version", {
            isOpen: true,
            value: "load_selection",
          });
          return;
        default:
          return;
      }
    },
    [
      activePrototypeCategoryForUi,
      addSelectedSituation,
      getIndexHitEntries,
      selectThought,
      setInterimState,
    ],
  );

  const handleStudioSituationLoadChange = useCallback(
    (situationId: string, isLoaded: boolean) => {
      setStudioSituationLoaded(
        activePrototypeCategoryForUi,
        situationId,
        isLoaded,
      );
    },
    [activePrototypeCategoryForUi, setStudioSituationLoaded],
  );

  const handleLoadAllStudioSituations = useCallback(() => {
    setStudioSituationsLoaded(
      activePrototypeCategoryForUi,
      studioSituationLoadManagerItems.map((item) => item.situationId),
      true,
    );
  }, [
    activePrototypeCategoryForUi,
    setStudioSituationsLoaded,
    studioSituationLoadManagerItems,
  ]);

  const handleTogglePreviewClick = useCallback(() => {
    if (!isLocked) {
      const lockResult = canLockExport();
      if (!lockResult.canLock) {
        showWarningToast(
          lockResult.reason ||
            "Cannot lock export. Please adjust your grouping, content, or profile selection.",
        );
        return;
      }
    }

    toggleActiveStudioExportLock();
  }, [canLockExport, isLocked, toggleActiveStudioExportLock]);

  const handleOpenRerankDialog = useCallback(() => {
    setInterimState("rerank_dialog", { isOpen: true });
  }, [setInterimState]);

  const handleExitMaintenanceMode = useCallback(() => {}, []);

  const handleDismissManualAddGuidance = useCallback(() => {
    resetInterimState("profile_shaping_guidance");
  }, [resetInterimState]);
  const handleDismissLoadedSelectionSearchGuidance = useCallback(() => {
    resetInterimState("profile_shaping_guidance");
  }, [resetInterimState]);

  useEffect(() => {
    if (!isLoadedSelectionSearchGuidanceActive) {
      wasLoadedSelectionSearchGuidanceActiveRef.current = false;
      loadedSelectionSearchGuidanceSeenRef.current = false;
      return;
    }

    if (!wasLoadedSelectionSearchGuidanceActiveRef.current) {
      loadedSelectionSearchGuidanceSeenRef.current = false;
    }

    wasLoadedSelectionSearchGuidanceActiveRef.current = true;

    if (!isLocked) {
      loadedSelectionSearchGuidanceSeenRef.current = true;
    }
  }, [isLoadedSelectionSearchGuidanceActive, isLocked]);

  useEffect(() => {
    if (
      isLoadedSelectionSearchGuidanceActive &&
      isLocked &&
      loadedSelectionSearchGuidanceSeenRef.current
    ) {
      resetInterimState("profile_shaping_guidance");
    }
  }, [isLoadedSelectionSearchGuidanceActive, isLocked, resetInterimState]);

  const studioQuickActions: StudioQuickAction[] = useMemo(
    () => [
      {
        key: "select_visible",
        label: "Select visible",
        icon: "select",
        humanReadable: isLocked
          ? "Return to search mode to update the profile selection"
          : "Select visible profile material",
        disabled: isLocked,
      },
      {
        key: "load_profile_version",
        label: "Load version",
        icon: "load",
        humanReadable: "Load profile version",
      },
    ],
    [isLocked],
  );

  return {
    activePrototypeCategoryForUi,
    activeStudioGroupingForUi,
    handlePrototypeCategoryChange,
    handleStudioGroupingChange,
    isLocked,
    studioQuickActions,
    handleStudioQuickActionClick,
    hasSelectionActionBar,
    selectedSituationsCountForUi,
    selectedThoughtsCountForUi,
    visibleSituationsCountForUi,
    visibleThoughtsCountForUi,
    reviewDescription,
    isPreviewUnavailable,
    isNeedsAnchorMode,
    maintenanceModeHumanReadable,
    previewButtonColorPalette,
    previewButtonIcon,
    previewButtonHumanReadable,
    shouldShowReviewDescription,
    handleTogglePreviewClick,
    handleOpenRerankDialog,
    handleExitMaintenanceMode,
    studioPrototypeItemsWithCounts,
    studioGroupingItems,
    shouldShowManualAddGuidance,
    handleDismissManualAddGuidance,
    shouldShowLoadedSelectionSearchGuidance,
    handleDismissLoadedSelectionSearchGuidance,
    studioSituationLoadManagerItems,
    loadedSituationManagerItemCount,
    availableSituationManagerItemCount,
    hiddenSituationManagerItemCount,
    handleStudioSituationLoadChange,
    handleLoadAllStudioSituations,
  };
}

export type StudioScreenState = ReturnType<typeof useStudioScreen>;
